/* Registering the service worker, and the two refusals that go with it.
 *
 * Wave 3 contract item 2. The worker itself is `app/public/sw.js`, written by
 * hand and copied verbatim into `dist/` by vite; this file is the twenty lines
 * that turn it on, and it is a module entry in `index.html` rather than an
 * inline script for a reason worth stating: the deployed Content Security
 * Policy is `script-src 'self'` with no `'unsafe-inline'`, so a registration
 * written into the page as a snippet would be blocked by the browser and the
 * app would silently have no worker. `deploy/Caddyfile` carries the header.
 *
 * **It refuses to register anywhere but a same-origin deployment.** Two cases,
 * and both of them have cost somebody an afternoon in some project or other.
 *
 * The vite dev server on 5174 proxies `/v1` to the API on 8077 and serves the
 * app itself with no hashed filenames. A worker registered there caches a
 * shell that changes on every save, so an edit stops appearing and the fix is
 * a hard reload the developer has to know to do. `import.meta.env.DEV` is off.
 *
 * The native builds have their own shell. `Capacitor.isNativePlatform()` is a
 * runtime check the capture path already makes; here the build-time constant
 * is enough, because a native build is produced by `npm run build:native` and
 * a worker inside a webview serving `capacitor://` has nothing to cache that
 * the app bundle does not already hold.
 *
 * **It never unregisters on failure and never reloads the page.** A worker
 * that fails to register is an app with no offline page, which is a degraded
 * app; a page that reloads itself because a worker updated is a parent halfway
 * through photographing a page losing the photograph. So the failure is a
 * console line and nothing else, and a new worker takes effect on the next
 * open. `skipWaiting` in the worker is what makes that the next open rather
 * than the one after.
 */

declare const __NATIVE_BUILD__: boolean | undefined;

/** True when this build is the web one served from our own origin. */
function shouldRegister(): boolean {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  if (typeof __NATIVE_BUILD__ !== "undefined" && __NATIVE_BUILD__) return false;
  if (import.meta.env?.DEV) return false;
  // A worker needs a secure context. `localhost` counts as one, which is what
  // lets the Playwright suite and a local Caddy both exercise this path.
  return window.isSecureContext;
}

export function registerServiceWorker(): void {
  if (!shouldRegister()) return;
  // After `load`, so the registration never competes with the first paint for
  // the connection. The app is usable before the worker exists; the worker
  // only changes what happens on the second open.
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((err) => {
      // A log line and nothing else. See the header: an app with no worker is
      // an app without an offline page, not a broken one.
      console.warn("the offline worker did not register", err);
    });
  });
}

registerServiceWorker();
